import React from 'react';
import { MapPin, Calendar, BookOpen, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { education } from '../mock';

const institutionLogos = {
  'Audencia Business School': '/audencia.webp',
  'Lycée Français St-Exupéry': '/lfse.png'
};

const Education = ({ isVisible }) => {
  return (
    <section id="education" data-section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div
          className={`transition-all duration-1000 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 text-center mb-4">
            Education
          </h2>
          <div className="w-24 h-1 bg-blue-700 mx-auto mb-12"></div>

          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
            {education.map((edu, index) => (
              <Card
                key={edu.id}
                className="border-2 border-slate-200 hover:border-blue-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                style={{
                  transitionDelay: `${index * 100}ms`
                }}
              >
                <CardHeader className="bg-slate-50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-white border border-slate-200">
                      {institutionLogos[edu.institution] ? (
                        <img
                          src={institutionLogos[edu.institution]}
                          alt={edu.institution}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full bg-blue-700 flex items-center justify-center">
                          <span className="text-white text-lg font-bold">{edu.institution.charAt(0)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg text-blue-900">{edu.degree}</CardTitle>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-slate-700">
                      <BookOpen size={16} className="text-blue-700" />
                      <span className="font-semibold">{edu.institution}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin size={16} className="text-blue-700" />
                      <span className="text-sm">{edu.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Calendar size={16} className="text-blue-700" />
                      <span className="text-sm">{edu.period}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-slate-700 mb-4">{edu.description}</p>

                  {edu.courses && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                        <Award size={16} />
                        Key Courses
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {edu.courses.map((course, idx) => (
                          <Badge key={idx} variant="secondary" className="bg-blue-100 text-blue-800">
                            {course}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {edu.specializations && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Specializations</h4>
                      <div className="flex flex-wrap gap-2">
                        {edu.specializations.map((spec, idx) => (
                          <Badge key={idx} variant="outline" className="border-blue-700 text-blue-700">
                            {spec}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {edu.activities && (
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">Activities & Achievements</h4>
                      <ul className="space-y-1">
                        {edu.activities.map((activity, idx) => (
                          <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                            <span className="text-blue-700">•</span>
                            <span>{activity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Education;
